/**
 * Карточка режима (ТЗ UI-42, UI-43).
 *
 * Было: ряд кнопок с одними названиями. «Тренировать символы» и «Печатать
 * начисто» ничего не говорят тому, кто их ещё не проходил, а выбирать
 * приходится именно ему.
 *
 * Стало: название, одна строка о том, чему режим учит, и состояние. Нажимается
 * вся карточка — цель нажатия размером с карточку, а не со строку.
 *
 * Закрытый режим остаётся живым: нажатие ведёт к объяснению, а не в никуда.
 * Так решено после первого живого использования — кнопка, которая не
 * отвечает, читается как сломанная. От ТЗ здесь отступление: оно просит
 * убрать закрытый режим из порядка обхода клавиатурой. Тогда причина стала бы
 * недостижимой с клавиатуры, а это дороже.
 */

import { useId } from 'react';
import type { Translate } from '@type2x/i18n';

export interface ModeCardProps {
  readonly t: Translate;
  /** Название режима: то же, что стоит заголовком его экрана. */
  readonly title: string;
  /** Одна строка о том, чему режим учит. */
  readonly purpose: string;
  /** Закрыт ли режим и почему: идентификатор объяснения рядом. */
  readonly locked?: { readonly hintId: string } | undefined;
  readonly onOpen: () => void;
}

export function ModeCard({ t, title, purpose, locked, onOpen }: ModeCardProps): React.JSX.Element {
  const closed = locked !== undefined;
  const titleId = useId();
  const purposeId = useId();

  /*
   * Имя карточки — название режима, а не весь её текст.
   *
   * Иначе диктор читает «Свой текст Ваш текст вместо нашего» одной строкой, и
   * из списка режимов не выбрать на слух. Назначение остаётся описанием: оно
   * читается следом, но не мешает выбирать.
   */
  return (
    <button
      type="button"
      className={`t2x-mode-card${closed ? ' t2x-mode-card--locked' : ''}`}
      aria-labelledby={titleId}
      aria-describedby={locked === undefined ? purposeId : `${purposeId} ${locked.hintId}`}
      onClick={onOpen}
    >
      <span className="t2x-mode-card__head">
        {/*
          Замок — знак, а не цвет: закрытое от открытого обязано отличаться и
          в оттенках серого (ТЗ UI-43). Слово «Закрыто» стоит рядом, потому
          что знак читает не всякий.
        */}
        {closed && (
          <span className="t2x-mode-card__lock" aria-hidden="true">
            🔒
          </span>
        )}
        <span className="t2x-mode-card__title" id={titleId}>
          {title}
        </span>
      </span>

      <span className="t2x-mode-card__purpose" id={purposeId}>
        {purpose}
      </span>

      {closed && <span className="t2x-mode-card__state">{t('common.locked')}</span>}
    </button>
  );
}
